import React, { useEffect, useState } from "react";
import { ArrowUpRight, Newspaper } from "lucide-react";
import { getMortgageNews } from "@/lib/api";

/**
 * Aggregated mortgage news — pulls latest articles from Mortgage Strategy
 * via our backend proxy (1h cache). Only shows if the feed returns items.
 */
function formatDate(s) {
    if (!s) return "";
    const d = new Date(s);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}

export default function MortgageNews() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sourceUrl, setSourceUrl] = useState(
        "https://www.mortgagestrategy.co.uk/"
    );

    useEffect(() => {
        let mounted = true;
        getMortgageNews()
            .then((d) => {
                if (!mounted) return;
                setItems((d.items || []).slice(0, 4));
                if (d.source_url) setSourceUrl(d.source_url);
            })
            .catch(() => setItems([]))
            .finally(() => mounted && setLoading(false));
        return () => {
            mounted = false;
        };
    }, []);

    if (!loading && items.length === 0) return null;

    return (
        <section
            data-testid="mortgage-news-section"
            className="relative py-14 md:py-20 bg-brand-surface border-t border-brand-border"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
                    <div className="md:col-span-6">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-brand-accent" />
                            <span className="text-xs uppercase tracking-[0.24em] text-brand-accent font-bold">
                                Market pulse
                            </span>
                        </div>
                        <h2 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-primary leading-[1.05]">
                            Latest mortgage
                            <br />
                            <em className="italic font-light text-brand-accent">
                                news.
                            </em>
                        </h2>
                    </div>
                    <div className="md:col-span-5 md:col-start-8 flex md:items-end">
                        <p className="text-lg text-brand-muted leading-relaxed">
                            Curated headlines from{" "}
                            <a
                                href={sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-brand-primary font-semibold link-underline"
                            >
                                Mortgage Strategy
                            </a>
                            . Refreshed hourly so you always see the market as it
                            moves.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                    {loading
                        ? Array.from({ length: 4 }).map((_, i) => (
                              <div
                                  key={i}
                                  className="h-[420px] rounded-[14px] bg-brand-surfaceAlt animate-pulse"
                              />
                          ))
                        : items.map((a, i) => (
                              <div>
                                  <a
                                      href={a.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      data-testid={`news-card-${i}`}
                                      className="group block h-full rounded-[14px] overflow-hidden border border-brand-border bg-brand-surface shadow-[0_18px_36px_-18px_rgba(30,79,138,0.18)] hover:shadow-[0_28px_48px_-16px_rgba(30,79,138,0.28)] hover:-translate-y-1 transition-all duration-500"
                                  >
                                      <div className="relative aspect-[4/3] overflow-hidden bg-brand-surfaceAlt">
                                          {a.image ? (
                                              <img
                                                  src={a.image}
                                                  alt=""
                                                  loading="lazy"
                                                  className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                                              />
                                          ) : (
                                              <div className="w-full h-full flex items-center justify-center text-brand-accent">
                                                  <Newspaper size={36} />
                                              </div>
                                          )}
                                      </div>
                                      <div className="p-6 flex flex-col">
                                          <div className="text-[11px] uppercase tracking-[0.2em] text-brand-accent font-bold mb-3">
                                              {formatDate(a.published) ||
                                                  "Mortgage Strategy"}
                                          </div>
                                          <h3 className="font-serif-editorial text-xl leading-snug text-brand-primary mb-3 line-clamp-3">
                                              {a.title}
                                          </h3>
                                          {a.summary && (
                                              <p className="text-sm text-brand-muted leading-relaxed line-clamp-3 mb-5">
                                                  {a.summary}
                                              </p>
                                          )}
                                          <div className="mt-auto inline-flex items-center gap-1.5 text-sm font-bold text-brand-primary link-underline">
                                              Read article
                                              <ArrowUpRight
                                                  size={14}
                                                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                                              />
                                          </div>
                                      </div>
                                  </a>
                              </div>
                          ))}
                </div>

                <div className="mt-10 text-center">
                    <a
                        href={sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="news-view-all"
                        className="inline-flex items-center gap-2 text-sm font-bold text-brand-primary link-underline"
                    >
                        View all mortgage news
                        <ArrowUpRight size={14} />
                    </a>
                </div>
            </div>
        </section>
    );
}
